import { Injectable, signal } from '@angular/core';
import * as L from 'leaflet';
import { forkJoin, timeout, catchError, of } from 'rxjs';
import { LayerService } from './layer.service';
import { WmsDataService } from './wms-data.service';
import { LayerDetails, OptionalLayer } from '../types';

export interface WmsInfoResult {
	layerTitle: string;
	serverName: string;
	data: Record<string, string>;
	error?: string;
	source?: 'wms' | 'geojson';
}

interface VisibleWmsLayer {
	baseUrl: string;
	layerName: string;
	displayTitle: string;
	serverName: string;
}

@Injectable({ providedIn: 'root' })
export class WmsInfoService {

	private map!: L.Map;
	private getMapLayers!: () => Record<string, any>;
	private clickHandler?: (e: L.LeafletMouseEvent) => void;

	wmsInfoActive = false;

	private _results = signal<WmsInfoResult[]>([]);
	results = this._results.asReadonly();

	private _loading = signal(false);
	loading = this._loading.asReadonly();

	private _clickPosition = signal<{ x: number; y: number } | null>(null);
	clickPosition = this._clickPosition.asReadonly();

	constructor(
		private ls: LayerService,
		private wmsDataService: WmsDataService
	) {}

	init(map: L.Map, getMapLayers: () => Record<string, any>): void {
		this.map = map;
		this.getMapLayers = getMapLayers;
	}

	startWmsInfoMode(): void {
		if (!this.map) return;
		this.wmsInfoActive = true;
		L.DomUtil.addClass(this.map.getContainer(), 'leaflet-crosshair');
		this.clickHandler = (e: L.LeafletMouseEvent) => this.onMapClick(e);
		this.map.on('click', this.clickHandler);
	}

	endWmsInfoMode(): void {
		if (!this.wmsInfoActive) return;
		this.wmsInfoActive = false;
		if (this.map) {
			L.DomUtil.removeClass(this.map.getContainer(), 'leaflet-crosshair');
			if (this.clickHandler) {
				this.map.off('click', this.clickHandler);
				this.clickHandler = undefined;
			}
		}
	}

	closeResults(): void {
		this._results.set([]);
		this._clickPosition.set(null);
		this._loading.set(false);
	}

	hasVisibleInfoLayers(): boolean {
		const mapDef = this.ls.mapDef();
		if (!mapDef) return false;
		return mapDef.layers.some(ld =>
			(ld.type === 'wms' && ld.display !== false && ld.layers?.some((l: OptionalLayer) => l.enabled)) ||
			(ld.type === 'geojson' && ld.display !== false)
		);
	}

	/** @deprecated Use hasVisibleInfoLayers() */
	hasVisibleWmsLayers(): boolean {
		return this.hasVisibleInfoLayers();
	}

	private onMapClick(e: L.LeafletMouseEvent): void {
		const containerPoint = this.map.latLngToContainerPoint(e.latlng);
		const mapRect = this.map.getContainer().getBoundingClientRect();
		this._clickPosition.set({
			x: mapRect.left + containerPoint.x,
			y: mapRect.top + containerPoint.y
		});

		// GeoJSON-Features am Klickpunkt sammeln
		const geojsonResults = this.getGeoJsonFeaturesAtPoint(e.latlng);

		const visibleLayers = this.getVisibleWmsLayers();
		if (visibleLayers.length === 0 && geojsonResults.length === 0) {
			this._results.set([]);
			this._loading.set(false);
			return;
		}

		// GeoJSON-Ergebnisse sofort anzeigen
		if (visibleLayers.length === 0) {
			this._results.set(geojsonResults);
			this._loading.set(false);
			return;
		}

		this._loading.set(true);
		this._results.set(geojsonResults);

		const requests = visibleLayers.map(vl =>
			this.wmsDataService.getWMSFeatureData(vl.baseUrl, vl.layerName, e.latlng, 'text/plain').pipe(
				timeout(10000),
				catchError(() => of(null))
			)
		);

		forkJoin(requests).subscribe(responses => {
			const results: WmsInfoResult[] = [...geojsonResults];
			responses.forEach((response, i) => {
				const vl = visibleLayers[i];
				if (response === null) {
					results.push({
						layerTitle: vl.displayTitle,
						serverName: vl.serverName,
						data: {},
						error: 'Abfrage fehlgeschlagen'
					});
				} else {
					const data = this.parseTextPlain(response);
					results.push({
						layerTitle: vl.displayTitle,
						serverName: vl.serverName,
						data
					});
				}
			});
			// Nur Layer mit Daten oder Fehlern anzeigen
			this._results.set(results.filter(r => r.error || Object.keys(r.data).length > 0));
			this._loading.set(false);
		});
	}

	private getVisibleWmsLayers(): VisibleWmsLayer[] {
		const result: VisibleWmsLayer[] = [];
		const mapDef = this.ls.mapDef();
		if (!mapDef) return result;
		const mapLayers = this.getMapLayers();

		for (const ld of mapDef.layers) {
			if (ld.type !== 'wms' || ld.display === false || !ld.layers) continue;

			for (const sublayer of ld.layers) {
				if (!sublayer.enabled) continue;

				const layerTitle = sublayer.title || sublayer.name;
				// Prüfen ob der Layer tatsächlich auf der Karte ist
				if (!mapLayers[layerTitle]) continue;

				// Prüfen ob der Layer sichtbar ist (nicht via CSS oder Pane versteckt)
				const paneName = `layer-${layerTitle.replace(/[^a-zA-Z0-9]/g, '_')}`;
				const pane = this.map?.getPane(paneName);
				if (pane?.style.display === 'none') continue;

				result.push({
					baseUrl: ld.url,
					layerName: sublayer.name,
					displayTitle: layerTitle,
					serverName: ld.name
				});
			}
		}
		return result;
	}

	private getGeoJsonFeaturesAtPoint(latlng: L.LatLng): WmsInfoResult[] {
		const results: WmsInfoResult[] = [];
		const mapDef = this.ls.mapDef();
		if (!mapDef) return results;
		const mapLayers = this.getMapLayers();

		for (const ld of mapDef.layers) {
			if (ld.type !== 'geojson' || ld.display === false) continue;

			const layerName = ld.displayName || ld.name;
			const paneName = `layer-${layerName.replace(/[^a-zA-Z0-9]/g, '_')}`;
			const pane = this.map?.getPane(paneName);
			if (pane?.style.display === 'none') continue;

			const leafletLayer = mapLayers[layerName];
			if (!leafletLayer || !(leafletLayer instanceof L.GeoJSON)) continue;

			// Alle Features im GeoJSON-Layer prüfen
			leafletLayer.eachLayer((featureLayer: any) => {
				if (typeof featureLayer.getBounds === 'function') {
					// Polygon/Polyline — prüfe ob der Klickpunkt innerhalb liegt
					const bounds = featureLayer.getBounds() as L.LatLngBounds;
					if (!bounds.contains(latlng)) return;

					// Für Polygone: präzisere Prüfung mit Ray-Casting
					if (typeof featureLayer.getLatLngs === 'function') {
						const latLngs = featureLayer.getLatLngs();
						if (!this.isPointInLayer(latlng, latLngs)) return;
					}
				} else if (typeof featureLayer.getLatLng === 'function') {
					// Punkt — prüfe Nähe (50px Toleranz)
					const point = this.map.latLngToContainerPoint(featureLayer.getLatLng());
					const clickPoint = this.map.latLngToContainerPoint(latlng);
					if (point.distanceTo(clickPoint) > 50) return;
				} else {
					return;
				}

				const props = featureLayer.feature?.properties;
				if (!props || Object.keys(props).length === 0) return;

				const data: Record<string, string> = {};
				for (const [key, value] of Object.entries(props)) {
					if (value != null && value !== '' && key !== 'style') {
						data[key] = String(value);
					}
				}

				if (Object.keys(data).length > 0) {
					results.push({
						layerTitle: layerName,
						serverName: 'GeoJSON',
						data,
						source: 'geojson'
					});
				}
			});
		}
		return results;
	}

	private isPointInLayer(point: L.LatLng, latLngs: any): boolean {
		// Unterstützt verschachtelte Arrays (Polygon mit Löchern, MultiPolygon)
		if (!Array.isArray(latLngs) || latLngs.length === 0) return false;

		// Einfaches Array von LatLng → einzelner Ring
		if (latLngs[0] instanceof L.LatLng) {
			return this.pointInRing(point, latLngs);
		}

		// Array von Ringen → Polygon (erster Ring = äußerer)
		if (latLngs[0][0] instanceof L.LatLng) {
			return this.pointInRing(point, latLngs[0]);
		}

		// Multi-Polygon → prüfe jeden Teil
		for (const polygon of latLngs) {
			if (Array.isArray(polygon) && polygon.length > 0) {
				const ring = polygon[0] instanceof L.LatLng ? polygon : polygon[0];
				if (this.pointInRing(point, ring)) return true;
			}
		}
		return false;
	}

	private pointInRing(point: L.LatLng, ring: L.LatLng[]): boolean {
		let inside = false;
		const x = point.lng, y = point.lat;
		for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
			const xi = ring[i].lng, yi = ring[i].lat;
			const xj = ring[j].lng, yj = ring[j].lat;
			if ((yi > y) !== (yj > y) && x < (xj - xi) * (y - yi) / (yj - yi) + xi) {
				inside = !inside;
			}
		}
		return inside;
	}

	private parseTextPlain(data: string): Record<string, string> {
		const result: Record<string, string> = {};
		if (!data || data.trim() === '') return result;

		const lines = data.split('\n');
		for (const line of lines) {
			const match = line.match(/^\s*([^=]+?)\s*=\s*(?:'([^']*)'|(.+))\s*$/);
			if (match) {
				const key = match[1].trim();
				const value = (match[2] !== undefined ? match[2] : match[3] || '').trim();
				if (value && value !== 'null' && value !== 'NULL') {
					result[key] = value;
				}
			}
		}
		return result;
	}
}
